import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check } from "lucide-react";
import clsx from "clsx";
import { ActionItems, Handoffs, Work } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { Deal, Handoff, WorkTask } from "@/api/types";
import { roleVar, stageRole, type Role } from "@/lib/colors";
import { fmtDayShort } from "@/lib/format";
import { toast } from "@/lib/feedback";
import { daysSince, todayISO } from "@/lib/people";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { Avatar } from "@/components/ui";
import InboxPanel from "@/components/assistant/InboxPanel";
import { useInbox } from "@/lib/useInbox";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Where you land each morning: only what needs you today. */
export default function TodayPage() {
  const me = useAuthStore((s) => s.user);
  const openDeal = useUIStore((s) => s.openDeal);
  const workQuery = useQuery({ queryKey: ["my-work"], queryFn: Work.mine });
  const today = todayISO();

  const work = workQuery.data;
  const dueTasks = (work?.tasks ?? []).filter((t) => t.due_date != null && t.due_date <= today);
  const laterTasks = (work?.tasks ?? []).filter((t) => t.due_date == null || t.due_date > today);
  // A deal whose next action is already covered by a due task would be a duplicate row.
  const dueTaskDeals = new Set(dueTasks.map((t) => t.deal_id));
  const dueDeals = (work?.due_deals ?? []).filter((d) => !dueTaskDeals.has(d.id));
  const handoffs = work?.handoffs ?? [];
  const quiet = work?.quiet_deals ?? [];
  const emailSuggestions = useInbox().data?.pending.length ?? 0;
  const needsYou =
    handoffs.length + dueTasks.length + dueDeals.length + quiet.length + emailSuggestions;

  const firstName = me?.name?.split(" ")[0];
  const dateLine = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="px-4 py-6 md:px-6 md:py-10">
      <div className="mx-auto max-w-[640px] space-y-8">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">
            {greeting()}
            {firstName ? `, ${firstName}` : ""}.
          </h1>
          <p className="text-md text-mut-500 mt-1">
            {dateLine}
            {work &&
              (needsYou === 0
                ? " · You're all caught up."
                : ` · ${plural(needsYou, "thing needs", "things need")} you.`)}
          </p>
        </header>

        <InboxPanel />

        {workQuery.isLoading ? (
          <p className="text-md text-mut-500">Loading…</p>
        ) : workQuery.isError ? (
          <p className="text-md text-accent-pink">
            Couldn't load your day: {apiErrorMessage(workQuery.error)}
          </p>
        ) : needsYou - emailSuggestions === 0 && laterTasks.length === 0 ? (
          <div className="panel p-8 text-center">
            <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-accent-green/15 text-accent-green">
              <Check className="w-5 h-5" />
            </div>
            <p className="text-base font-medium">Nothing else needs you right now</p>
            <p className="text-md text-mut-500 mt-1">
              Handoffs, tasks assigned to you and deals going quiet will show up here.
            </p>
          </div>
        ) : (
          <>
            {handoffs.length > 0 && (
              <Group title="Handed to you" role="violet">
                {handoffs.map((h) => (
                  <HandoffRow key={h.id} handoff={h} onOpen={() => openDeal(h.deal_id)} />
                ))}
              </Group>
            )}

            {(dueTasks.length > 0 || dueDeals.length > 0) && (
              <Group title="Due" role="azure">
                {dueTasks.map((t) => (
                  <TaskRow key={t.id} task={t} today={today} onOpen={() => openDeal(t.deal_id)} />
                ))}
                {dueDeals.map((d) => (
                  <DealDueRow key={d.id} deal={d} today={today} onOpen={() => openDeal(d.id)} />
                ))}
              </Group>
            )}

            {quiet.length > 0 && (
              <Group title="Going quiet" role="amber">
                {quiet.map((d) => (
                  <button
                    key={d.id}
                    className="row-hover w-full flex items-start gap-3 p-3 text-left"
                    onClick={() => openDeal(d.id)}
                  >
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent-amber ring-4 ring-accent-amber/20" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-md font-medium">{d.lead}</span>
                      <span className="block text-sm text-mut-500">
                        No contact in {daysSince(d.last_contact) ?? "many"} days
                        {d.account_name ? ` · ${d.account_name}` : ""}
                      </span>
                    </span>
                    <span className="chip-tint mt-0.5" style={roleVar("amber")}>
                      Reach out
                    </span>
                  </button>
                ))}
              </Group>
            )}

            {laterTasks.length > 0 && (
              <Group title="Coming up" role="cyan">
                {laterTasks.slice(0, 8).map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    today={today}
                    compact
                    onOpen={() => openDeal(t.deal_id)}
                  />
                ))}
                {laterTasks.length > 8 && (
                  <p className="px-3 text-sm text-mut-500">and {laterTasks.length - 8} more</p>
                )}
              </Group>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Group({
  title,
  role,
  children,
}: {
  title: string;
  role: Role;
  children: React.ReactNode;
}) {
  return (
    <section style={roleVar(role)}>
      <h2
        className="mb-1.5 flex items-center gap-2 text-2xs font-mono font-medium uppercase tracking-[.1em]"
        style={{ color: "rgb(var(--c))" }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: "rgb(var(--c))" }} />
        {title}
      </h2>
      <div className="space-y-0.5">{children}</div>
    </section>
  );
}

function HandoffRow({ handoff: h, onOpen }: { handoff: Handoff; onOpen: () => void }) {
  const qc = useQueryClient();
  const done = (msg: string) => () => {
    void qc.invalidateQueries({ queryKey: ["my-work"] });
    void qc.invalidateQueries({ queryKey: ["deals"] });
    void qc.invalidateQueries({ queryKey: ["handoffs"] });
    void qc.invalidateQueries({ queryKey: ["deal", h.deal_id] });
    void qc.invalidateQueries({ queryKey: ["deal-handoffs", h.deal_id] });
    toast.ok(msg);
  };
  const accept = useMutation({
    mutationFn: () => Handoffs.accept(h.id),
    onSuccess: done(`${h.deal_lead ?? "The deal"} is yours now.`),
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const decline = useMutation({
    mutationFn: () => Handoffs.decline(h.id),
    onSuccess: done("Handoff declined."),
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const busy = accept.isPending || decline.isPending;
  return (
    <div className="rounded-xl p-3.5 flex gap-3" style={{ background: "var(--grad-handoff)" }}>
      <span className="flex items-center gap-1 shrink-0 pt-0.5">
        <Avatar name={h.from_name} size={24} />
        <ArrowRight className="w-3 h-3 text-accent-violet" />
        <Avatar name={h.to_name} size={24} />
      </span>
      <button className="min-w-0 flex-1 text-left" onClick={onOpen}>
        <span className="block text-md font-medium hover:underline">{h.deal_lead}</span>
        <span className="block text-sm text-mut-300 whitespace-pre-line line-clamp-3">
          {h.note ? h.note : `${h.from_name ?? "A teammate"} didn't leave a note.`}
        </span>
        <span className="block text-2xs text-mut-500 mt-1">
          From {h.from_name ?? "a teammate"} · {fmtDayShort(h.created_at)} · {h.stage}
        </span>
      </button>
      <span className="flex flex-col gap-1.5 shrink-0">
        <button className="btn btn-primary !py-1" disabled={busy} onClick={() => accept.mutate()}>
          Accept
        </button>
        <button className="btn !py-1" disabled={busy} onClick={() => decline.mutate()}>
          Decline
        </button>
      </span>
    </div>
  );
}

function dueLabel(due: string | null, today: string): { text: string; late: boolean } {
  if (!due) return { text: "No due date", late: false };
  if (due < today) return { text: `Overdue since ${fmtDayShort(due)}`, late: true };
  if (due === today) return { text: "Due today", late: true };
  return { text: fmtDayShort(due), late: false };
}

function TaskRow({
  task,
  today,
  compact = false,
  onOpen,
}: {
  task: WorkTask;
  today: string;
  compact?: boolean;
  onOpen: () => void;
}) {
  const qc = useQueryClient();
  const complete = useMutation({
    mutationFn: () => ActionItems.update(task.deal_id, task.id, { status: "done" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["my-work"] });
      void qc.invalidateQueries({ queryKey: ["action-items", task.deal_id] });
      toast.ok("Done.");
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const due = dueLabel(task.due_date, today);
  return (
    <div className={clsx("row-hover flex items-start gap-3", compact ? "px-3 py-2" : "p-3")}>
      <button
        className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded-md border-[1.5px] border-accent-azure grid place-items-center hover:bg-accent-azure/10 disabled:opacity-50"
        aria-label={`Mark "${task.text}" done`}
        disabled={complete.isPending}
        onClick={() => complete.mutate()}
      >
        {complete.isPending && <Check className="w-3 h-3 text-accent-azure" />}
      </button>
      <button className="min-w-0 flex-1 text-left" onClick={onOpen}>
        <span className={clsx("block font-medium", compact ? "text-sm" : "text-md")}>
          {task.text}
        </span>
        <span className="block text-sm text-mut-500">
          {task.deal_lead}
          {" · "}
          <span className={clsx(due.late && "text-accent-pink font-medium")}>{due.text}</span>
        </span>
      </button>
    </div>
  );
}

function DealDueRow({ deal, today, onOpen }: { deal: Deal; today: string; onOpen: () => void }) {
  const due = dueLabel(deal.due_date, today);
  return (
    <button className="row-hover w-full flex items-start gap-3 p-3 text-left" onClick={onOpen}>
      <span
        className="mt-1 h-3 w-3 shrink-0 rounded-full border-2"
        style={{ borderColor: `rgb(var(--${stageRole(deal.stage)}))` }}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-md font-medium">{deal.next_action || "Next step"}</span>
        <span className="block text-sm text-mut-500">
          {deal.lead} · {deal.stage} ·{" "}
          <span className={clsx(due.late && "text-accent-pink font-medium")}>{due.text}</span>
        </span>
      </span>
    </button>
  );
}
